import React from 'react';

import {
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	type IntlShape,
	type MessageDescriptor,
} from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import { selectUnit } from '@atlassian/recent-work-ui';

import { isMoreThanOneWeekAgo } from '../../../../../common/ui/event-type-message';
import { type ActivityEnrichment } from '../../../../../common/ui/quick-find/types';

import {
	clickedEventTypeMessages,
	commentedEventTypeMessages,
	createdEventTypeMessages,
	editedEventTypeMessages,
	type EventTypeMessages,
	icuClickedEventTypeMessages,
	icuCommentedEventTypeMessages,
	icuCreatedEventTypeMessages,
	icuEditedEventTypeMessages,
	icuInteractedEventTypeMessages,
	icuMentionedEventTypeMessages,
	icuReactedEventTypeMessages,
	icuReadEventTypeMessages,
	icuSharedEventTypeMessages,
	icuViewedEventTypeMessages,
	interactedEventTypeMessages,
	mentionedEventTypeMessages,
	messages,
	reactedEventTypeMessages,
	readEventTypeMessages,
	sharedEventTypeMessages,
	viewedEventTypeMessages,
} from './messages';
import { parseRecommendedReason, RecommendedEventType, RecommendedType } from './types';
import { resolveDisplayTimestamp } from './utils';

const LOCAL_EVENT_TYPE_MESSAGES: Record<RecommendedEventType, EventTypeMessages> = {
	clicked: clickedEventTypeMessages,
	commented: commentedEventTypeMessages,
	created: createdEventTypeMessages,
	edited: editedEventTypeMessages,
	interacted: interactedEventTypeMessages,
	mentioned: mentionedEventTypeMessages,
	reacted: reactedEventTypeMessages,
	read: readEventTypeMessages,
	shared: sharedEventTypeMessages,
	viewed: viewedEventTypeMessages,
};

const ICU_EVENT_TYPE_MESSAGES: Record<RecommendedEventType, EventTypeMessages> = {
	clicked: icuClickedEventTypeMessages,
	commented: icuCommentedEventTypeMessages,
	created: icuCreatedEventTypeMessages,
	edited: icuEditedEventTypeMessages,
	interacted: icuInteractedEventTypeMessages,
	mentioned: icuMentionedEventTypeMessages,
	reacted: icuReactedEventTypeMessages,
	read: icuReadEventTypeMessages,
	shared: icuSharedEventTypeMessages,
	viewed: icuViewedEventTypeMessages,
};

/**
 * Returns the appropriate message descriptor for a given event type.
 * @param eventType - The type of the event.
 * @param options - Options to configure the message.
 * @returns The message descriptor for the event type.
 */
const messageForEventType = (
	eventType: RecommendedEventType,
	options: { isAbsolute?: boolean; useIcu?: boolean } = {},
): MessageDescriptor => {
	const { isAbsolute = true, useIcu = false } = options;
	const messageMap = useIcu ? ICU_EVENT_TYPE_MESSAGES : LOCAL_EVENT_TYPE_MESSAGES;
	const fallback = useIcu ? icuViewedEventTypeMessages : viewedEventTypeMessages;
	const eventTypeMessage = messageMap.hasOwnProperty(eventType) ? messageMap[eventType] : fallback;

	return isAbsolute ? eventTypeMessage.absolute : eventTypeMessage.relative;
};

// Sort enum values by length descending to avoid substring false positives,
const sortedRecommendedEventTypes = Object.values(RecommendedEventType).sort(
	(a, b) => b.length - a.length,
);

/**
 * Returns the `feature` value from `reason` if it matches one of the keys in
 * `LOCAL_EVENT_TYPE_MESSAGES` (case-insensitive), otherwise returns `null`.
 */
export const getEventTypeFromReasonFeature = (
	feature: string | undefined,
): RecommendedEventType | null => {
	if (!feature) {
		return null;
	}
	const lowerFeature = feature.toLowerCase();
	if (LOCAL_EVENT_TYPE_MESSAGES.hasOwnProperty(lowerFeature)) {
		return lowerFeature as RecommendedEventType;
	}
	return null;
};

/**
 * Extracts the event type from `reason.text` by checking if the text contains
 * a known event type keyword. Returns the matching `RecommendedEventType` value,
 * or `RecommendedEventType.VIEWED` as a default fallback.
 */
export const extractEventTypeFromReasonText = (text: string): RecommendedEventType => {
	const lowerText = text.toLowerCase();

	for (const eventType of sortedRecommendedEventTypes) {
		if (lowerText.includes(eventType)) {
			return eventType;
		}
	}

	return RecommendedEventType.VIEWED;
};

type RecommendedDocumentEventTypeMessageProps = {
	reason: string;
	activityEnrichment?: ActivityEnrichment;
	intl: IntlShape;
	logException: (error: Error) => Promise<void>;
};

export const RecommendedDocumentEventTypeMessage = ({
	reason,
	activityEnrichment,
	intl,
	logException,
}: RecommendedDocumentEventTypeMessageProps): React.ReactNode => {
	const parsedReason = parseRecommendedReason(reason, logException);

	if (!parsedReason) {
		return null;
	}

	const eventType =
		getEventTypeFromReasonFeature(parsedReason.feature) ??
		extractEventTypeFromReasonText(parsedReason.text);

	const { type } = parsedReason;

	const timestamp = resolveDisplayTimestamp(
		type,
		eventType,
		parsedReason.timestamp,
		activityEnrichment,
	);

	const useIcu = fg('qf_icu_select_event_type_messages');

	const isAbsolute = isMoreThanOneWeekAgo(timestamp);

	const message = messageForEventType(eventType, { isAbsolute, useIcu });

	const { value, unit } = selectUnit(new Date(timestamp).getTime(), new Date(), {
		day: 7,
	});

	const values = isAbsolute
		? {
				// Show the absolute time if the activity was more than a week ago
				// e.g: You viewed on January 1, 2021
				absoluteTime: (
					<FormattedDate value={timestamp} month="short" day="2-digit" year="numeric" />
				),
			}
		: {
				// Show the relative time if the activity was within a week ago
				// e.g: You viewed 2 hours ago
				relativeTime: <FormattedRelativeTime value={value} unit={unit} />,
			};

	if (useIcu) {
		// New: use ICU select so translators get full sentence context and verb conjugation is correct.
		// `actor` is 'you' or 'collaborator'; `username` is the `other` branch for real names in future.
		const actor = type === RecommendedType.NO_COLLABORATOR ? 'you' : 'collaborator';
		return <FormattedMessage {...message} values={{ ...values, actor, username: '' }} />;
	}

	// Legacy: pre-format the subject as a string and inject it into the message.
	const collaborator =
		type === RecommendedType.NO_COLLABORATOR
			? intl.formatMessage(messages.you)
			: intl.formatMessage(messages.collaborator);
	return <FormattedMessage {...message} values={{ ...values, collaborator }} />;
};
